import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import Layout from './components/Layout';
import RoleBasedRoute from './components/RoleBasedRoute';
import LanguageSwitcher from './components/LanguageSwitcher';
import ErrorBoundary from './components/ErrorBoundary';
import { Mail, Lock, User, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react';

const Home = React.lazy(() => import('./pages/Home'));
const CarpetDetail = React.lazy(() => import('./pages/CarpetDetail'));
const AddCarpet = React.lazy(() => import('./pages/AddCarpet'));
const EditCarpet = React.lazy(() => import('./pages/EditCarpet'));
const SoldCarpets = React.lazy(() => import('./pages/SoldCarpets'));

const RouteFallback: React.FC = () => {
  const { t } = useLocale();

  return (
    <div className="flex items-center justify-center min-h-[400px] pt-4">
      <div className="text-center">
        <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    </div>
  );
};

const AuthenticatedApp: React.FC = () => {
  const { user, loading } = useAuth();

  // Show login screen if no user (including during initial loading)
  if (!user) {
    return <LoginScreen />;
  }

  // Show loading only when we have a user but still loading user profile/permissions
  if (loading) {
    return (
      <Layout>
        <RouteFallback />
      </Layout>
    );
  }

  // Show authenticated app with navbar
  return (
    <Layout>
      <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={
            <ErrorBoundary>
              <Home />
            </ErrorBoundary>
          } />
          <Route path="/carpet/:id" element={
            <ErrorBoundary>
              <CarpetDetail />
            </ErrorBoundary>
          } />
          <Route path="/add" element={
            <ErrorBoundary>
              <RoleBasedRoute allowedRoles={['admin', 'admin_light']}>
                <AddCarpet />
              </RoleBasedRoute>
            </ErrorBoundary>
          } />
          <Route path="/edit/:id" element={
            <ErrorBoundary>
              <RoleBasedRoute allowedRoles={['admin']}>
                <EditCarpet />
              </RoleBasedRoute>
            </ErrorBoundary>
          } />
          <Route path="/sold" element={
            <ErrorBoundary>
              <RoleBasedRoute allowedRoles={['admin', 'admin_light']}>
                <SoldCarpets />
              </RoleBasedRoute>
            </ErrorBoundary>
          } />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
};

const LoginScreen: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { signIn } = useAuth();
  const { t } = useLocale();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signIn(email, password);
    } catch (err: any) {
      setError(err.message || t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center overflow-hidden">
      <div className="w-full max-w-md h-full flex items-center justify-center p-4">
        {/* Login Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full relative">
          {/* Language Switcher - Top Right of Card */}
          <div className="absolute top-6 right-6">
            <LanguageSwitcher />
          </div>
          
          {/* Logo/Brand Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <span className="text-white text-lg font-bold tracking-tight">MC53</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
              {t('auth.title')}
            </h1>
            <p className="text-gray-600 text-sm font-medium mb-1">
              {t('auth.subtitle')}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start animate-in slide-in-from-top duration-300">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-red-700 text-sm leading-relaxed">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth.emailLabel')}
              </label>
              <div className="relative group">
                <Mail className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors group-focus-within:text-blue-500" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-gray-50/50 focus:bg-white placeholder-gray-400"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.passwordLabel')}
              </label>
              <div className="relative group">
                <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors group-focus-within:text-blue-500" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-gray-50/50 focus:bg-white placeholder-gray-400"
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3.5 px-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  {t('auth.signingIn')}
                </>
              ) : (
                <>
                  <User className="w-5 h-5 mr-2" />
                  {t('auth.signIn')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <LocaleProvider>
        <AuthProvider>
          <Router>
            <ErrorBoundary>
              <AuthenticatedApp />
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </LocaleProvider>
    </ErrorBoundary>
  );
}

export default App; 